import React, { useCallback, useRef, useState } from 'react';
import s from './s.module.less';
import TimezoneOperationhours from 'components/Calendar/TimezoneOperationhours';
import AddOverrideModal from 'components/Calendar/DatesOverride/components/AddOverrideModal';
import AvailabilityWarningModal from 'components/Calendar/TimezoneOperationhours/components/AvailabilityWarningModal';
import type { IProviderOverrideListWithDates } from 'components/Calendar/DatesOverride/components/AddOverrideModal/types';
import { Button, Divider, Form, message, Spin } from 'antd';
import useOverride from 'hooks/useOverride';
import { CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { IproviderOverrideList, TimeItemType } from 'types/appointmentSetting';
import { transferTimePicker } from 'components/Calendar/DatesOverride/utils';
import { providerOverrideDelete } from 'api/appointmentSetting';
import InfoNote from 'components/InfoNote';

interface IProps {
    formId?: string;
    onSaveStep?: () => void;
}

const Step2 = ({
    formId,
    onSaveStep,
}: IProps) => {
    const [form] = Form.useForm();
    const [overrideList, loading, fetchData] = useOverride();
    const [openAddAppointmentModal, setOpenAddAppointmentModal] = useState(false);
    const [editItem, setEditItem] = useState<IProviderOverrideListWithDates | null>(null);
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [missingDays, setMissingDays] = useState<string[]>([]);
    const timezoneOperationhoursRef = useRef<any>(null);
    const onCancel = useCallback(() => {
        setOpenAddAppointmentModal(false);
    }, []);

    const handleFetchData = useCallback(() => {
        setOpenAddAppointmentModal(false);
        fetchData();
    }, [fetchData]);

    const deleteBlockoutDatesItem = async (item: IproviderOverrideList) => {
        try {
            const { id } = item;
            if (id) {
                const { data } = await providerOverrideDelete(id);
                if (data.code === 200) {
                    message.success(data.message);
                    fetchData();
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleContinueAnyway = useCallback(async () => {
        setShowWarningModal(false);
        setMissingDays([]);
        await timezoneOperationhoursRef.current?.onUpdate();
        onSaveStep?.();
    }, [onSaveStep]);

    const handleAddAvailability = useCallback(() => {
        setShowWarningModal(false);
        setMissingDays([]);
    }, []);

    const handleFinish = useCallback(async () => {
        const missingDaysList = timezoneOperationhoursRef.current?.getMissingDays?.() || [];

        if (missingDaysList.length > 0) {
            setMissingDays(missingDaysList);
            setShowWarningModal(true);
        } else {
            await timezoneOperationhoursRef.current?.onUpdate();
            onSaveStep?.();
        }
    }, [onSaveStep]);

    return (
        <div className={s.wrap}>
            <Spin spinning={loading}>
                <div className={s.content}>
                    <div className={s.header}>
                        <h1>Set up your working hours</h1>
                        <div className={s.headerDescription}>
                            Configure when you&apos;re available to see patients. You can always change this later.
                        </div>
                    </div>
                    <InfoNote content="Did you know? Over 75% of patients book within 48 hours. Keeping your calendar open every day, even for just an hour, helps you attract more new patients." />
                    <Form
                        form={form}
                        name={formId}
                        onFinish={handleFinish}
                        id={formId}
                    />
                    <TimezoneOperationhours autoSave={false} ref={timezoneOperationhoursRef} />
                    <Divider className={s.divider} />
                    <Button
                        className={s.addBtn}
                        onClick={() => {
                            setOpenAddAppointmentModal(true);
                        }}
                    >+ Add an override
                    </Button>
                    <div className="mt-7 mb-7">
                        {
                            overrideList && overrideList.map((item, index) => {
                                const timeArr = item?.time;
                                return (
                                    <div className="flex pl-6 pr-6 pt-4 pb-4 items-center justify-between w-2/3 mh-24 mb-4 border-gray-300 border-solid shadow-sm rounded-lg" style={{ borderWidth: '1px' }} key={index}>
                                        <div className="flex flex-col text-sm text-gray-500">
                                            <span className="text-gray-900 font-medium text-sm">{dayjs(item.overrideDate).format('dddd, MMMM DD, YYYY')}</span>
                                            {
                                                timeArr && timeArr?.map((i: TimeItemType, idx: number) => {
                                                    return <span className="text-sm font-normal text-gray-500" key={idx}><i className="mr-1">·</i>{transferTimePicker(i)}</span>;
                                                })
                                            }
                                        </div>
                                        <CloseOutlined className="w-6 h-6 text-gray-400" onClick={() => deleteBlockoutDatesItem(item)} />
                                    </div>
                                );
                            })
                        }
                    </div>
                    {
                        openAddAppointmentModal && <AddOverrideModal onCancel={onCancel} onFetchData={handleFetchData} editItem={editItem} />
                    }
                    <AvailabilityWarningModal
                        open={showWarningModal}
                        missingDays={missingDays}
                        onAddAvailability={handleAddAvailability}
                        onContinue={handleContinueAnyway}
                    />
                </div>
            </Spin>
        </div>
    );
};

export default Step2;
